import { useState, useEffect } from 'react';
import { useComponent } from '@livefree/runtime';
import { useDefaultApp, useUser } from '@livefree/applications';
import { Outlet, useNavigate } from 'react-router';
import { Accordion, Dropdown, Label, Separator } from '@heroui/react';
import {
  CreateItemButton,
  DefaultLayout,
  Main,
  AppSwitcher,
  SidebarMain,
  MainNavigation,
  Menu,
  useThemeProvider,
} from '@livefree/react-ui';
import { useOAuth2 } from '@livefree/react-oauth2';
import { hostRuntime, ShellConfig } from '@/core';
import { RecentViewsBar } from '@/components/RecentViewsBar';

export const ShellLayout = () => {
  // The Accounts app's search panel, from the component registry: the host imports apps only to
  // call their init.
  const AccountSearchPanel = useComponent('accounts.search-panel');
  const [navigation, setNavigation] = useState([]);
  const navigate = useNavigate();
  const { signOut } = useOAuth2();
  const user = useUser();
  const [defaultApp, _, updateDefaultApp] = useDefaultApp();
  const { logo, icon } = useThemeProvider();
  const apps = hostRuntime.getApps();

  return (
    <DefaultLayout onOpenChanged={() => {}}>
      <DefaultLayout.Sidebar>
        <DefaultLayout.Sidebar.Header
          logo={`https://fwupsblob.blob.core.windows.net/img/${logo}`}
          closedLogo={`https://fwupsblob.blob.core.windows.net/img/${icon}`}
        />

        <SidebarMain className="p-4 px-2">
          {apps.map(app => (
            <CreateItemButton
              key={app.id}
              baseUrl={app.baseUrl}
              items={app.resources?.()?.items ?? []}
              handlers={app.resources?.()?.handlers(navigate) ?? []}
            />
          ))}
          <MainNavigation
            items={apps}
            defaultExpandedKeys={apps.reduce(
              (prev, curr) =>
                window.location.pathname.startsWith(curr.baseUrl) ? [...prev, curr.id] : prev,
              [],
            )}
          />
        </SidebarMain>

        <DefaultLayout.Sidebar.Footer>
          <DefaultLayout.Sidebar.Footer.Profile
            name={user.displayName}
            email={user.email}
            status="active"
          >
            <Dropdown.Section>
              <Label slot="header">Settings</Label>
              <Dropdown.Item
                key="profile"
                id="profile"
                textValue="My Profile"
                onPress={() => (window.location = ShellConfig.current.auth.host)}
              >
                <i className="ri-account-circle-line" />
                <Label>My Profile</Label>
              </Dropdown.Item>
            </Dropdown.Section>
            <Separator />
            <Dropdown.Section>
              <Label slot="header">Sign Out</Label>
              <Dropdown.Item
                key="signout"
                id="signout"
                textValue="Sign Out"
                onPress={() => signOut()}
              >
                <i className="ri-logout-circle-line" />
                <Label>Sign Out</Label>
              </Dropdown.Item>
            </Dropdown.Section>
          </DefaultLayout.Sidebar.Footer.Profile>
        </DefaultLayout.Sidebar.Footer>
      </DefaultLayout.Sidebar>

      <DefaultLayout.Header className="w-full" hideLang hideNotifs>
        <div className="flex items-center h-full w-full relative">
          <div className="basis-[70%] relative"></div>
          <div className="basis-[30%] flex items-center justify-end gap-2">
            <AccountSearchPanel />
            <AppSwitcher className="w-auto">
              {apps
                .filter(
                  app =>
                    app.description.isVisible(app.runtime) &&
                    app.navigation().isVisible &&
                    app.navigation().items.some(x => x.isVisible),
                )
                .map(app => (
                  <AppSwitcher.App
                    key={app.id}
                    id={app.id}
                    title={app.description.title}
                    iconClass={app.description.iconClass}
                    canFavorite
                    isFavorite={defaultApp?.id === app.id}
                    onFavorite={() =>
                      updateDefaultApp({
                        id: app.id,
                        path: app.baseUrl + app.description.defaultRoute,
                        title: app.description.title,
                        iconClass: app.description.iconClass,
                      })
                    }
                    onFavoriteText="Set as default app"
                    onUnfavoriteText="Unset as default app"
                    onPress={() => navigate(app.baseUrl + app.description.defaultRoute)}
                  />
                ))}
            </AppSwitcher>
          </div>
        </div>
      </DefaultLayout.Header>
      <DefaultLayout.ContentHeader className="p-0">
        <RecentViewsBar userId={user.id} />
      </DefaultLayout.ContentHeader>
      <Main>
        <Outlet />
      </Main>
    </DefaultLayout>
  );
};
